import type { Placement } from '@floating-ui/react';
import type { RefObject } from 'react';
import { useRef } from 'react';
import { useClickOutside } from '@/hooks/useClickOutside';
import { usePopup } from './hooks/usePopup';

interface PopupProps {
  trigger: React.ReactNode | ((isOpen: boolean, close: VoidFunction, toggle: VoidFunction) => React.ReactNode);
  children: (close: VoidFunction, toggle: VoidFunction) => React.ReactNode;
  placement?: Placement;
  onToggleTrigger?: boolean;
  triggerWrapperClassName?: string;
}

export const Popup: React.FC<PopupProps> = ({
  trigger,
  children,
  placement = 'bottom-start',
  onToggleTrigger = true,
  triggerWrapperClassName,
}) => {
  const { isOpen, open, close, toggle, x, y, reference, floating, strategy } = usePopup(placement);
  const popupRef = useRef<HTMLDivElement>(null);
  useClickOutside(popupRef as RefObject<HTMLDivElement>, close);

  return (
    <div ref={popupRef}>
      <div ref={reference} onClick={onToggleTrigger ? toggle : open} className={triggerWrapperClassName} role="button">
        {typeof trigger === 'function' ? trigger(isOpen, close, toggle) : trigger}
      </div>

      {isOpen && (
        <div
          ref={floating}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            zIndex: 1000,
          }}
        >
          {children(close, toggle)}
        </div>
      )}
    </div>
  );
};